import { useQuery } from '@tanstack/react-query';

import { graphql, type ResultOf, type VariablesOf } from '@/graphql';
import { getCartIdCookie, saveCartIdCookie } from '@/utils/cartUtils';
import gqlClient from '@/utils/gqlClient';
import isBrowser from '@/utils/isBrowser';

export const query = graphql(`
  query RefreshCart($cartId: String) {
    cart(cartId: $cartId) {
      refreshCart {
        resultCategory
        cartId
        cartIdChangeReason
        cart {
          lastUpdatedAt
          promotions {
            promotionCode
            campaign
          }
          lineItems {
            skuCode
            quantity
            sku {
              bbySkuId
            }
          }
          isWarrantyReplacement
          itemCountForIcon
          maxQuantityByBaseSku
          maxAddOnQuantityByBaseSku
          maxRingQuantity
          maxChargerQuantity
          ringCount
          chargerCount
          digitalSized
          creationTags {
            vendorId
          }
        }
      }
    }
  }
`);

export type RefreshCartResult = ResultOf<typeof query>;
export type RefreshCartVariables = VariablesOf<typeof query>;

export function withQueryKey(vars: RefreshCartVariables) {
  return {
    queryKey: ['refreshCart', vars.cartId],
    queryFn: async () => {
      const data = await gqlClient().request(query, vars);

      const refreshResponse = data.cart?.refreshCart;

      if (
        refreshResponse?.resultCategory === 'success' &&
        refreshResponse.cartId
      ) {
        saveCartIdCookie(refreshResponse.cartId);
      }

      return data;
    },
  };
}

export function getStaleTime(): number {
  return isBrowser() ? 5 * 60 * 1000 : Infinity;
}

export function useRefreshCart(fetchingDiscounts = true) {
  const cartId = getCartIdCookie();
  return useQuery({
    ...withQueryKey({ cartId }),
    // defer refreshing of cart until promotions have been updated to avoid race conditions
    enabled: !fetchingDiscounts,
    // Keep data cached for 5 minutes on the browser.
    staleTime: getStaleTime(), // Do not manually garbage collect on the server to avoid excessive memory usage
  });
}
